import { zodResolver } from "@hookform/resolvers/zod";
import React, { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../components/inputs/Button";
import Dropdown from "../components/inputs/Dropdown";
import Input from "../components/inputs/Input";
import AddRepModal from "../components/panels/AddRepModal";
import InviteRepModal from "../components/panels/InviteRepModal";
import ReusableTable from "../components/shared/ReusableTable";
import Textarea from "../components/shared/Textarea";
import { useAuth } from "../hooks/useAuth";
import { panelAPI } from "../services/api";
import { cadreAPI } from "../services/cadre.service";
import notificationService from "../services/notification.service";
import {
  panelCreationDefaultValues,
  panelCreationSchema,
} from "../types/panelValidationSchema";
import {
  EXPERTISE_OPTIONS,
  FEMA_REGION_OPTIONS,
  PANEL_STATUS_OPTIONS,
  PANEL_TYPE_OPTIONS,
  PROJECT_TYPE_REP_REQUIREMENTS,
  US_STATES,
} from "../utils/constants";

// Available cadre members are loaded from API

const PanelCreationEnhanced = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);

  // React Hook Form setup
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(panelCreationSchema),
    defaultValues: {
      ...panelCreationDefaultValues,
      createdBy: user?.id,
      createdDate: new Date().toISOString().split("T")[0],
    },
    mode: "onChange",
  });

  // Collapsible section states
  const [collapsedSections, setCollapsedSections] = useState({
    panelInfo: false,
    summary: false,
    decision: false,
    finalDetermination: false,
    shortlistFilters: false,
    reviewDates: false,
  });

  const [availableCadre, setAvailableCadre] = useState([]);
  const [filteredCadre, setFilteredCadre] = useState([]);
  const [selectedCadre, setSelectedCadre] = useState([]);
  const [filterCriteria, setFilterCriteria] = useState({
    expertise: [],
    yearsExperience: "",
    region: "",
  });
  const [createdPanelId, setCreatedPanelId] = useState(null);
  const [shortlistLoading, setShortlistLoading] = useState(false);

  // Step 4: Add Reps state
  const [reps, setReps] = useState([]);
  const [showAddRepModal, setShowAddRepModal] = useState(false);
  const [showInviteRepModal, setShowInviteRepModal] = useState(false);
  const [repLoading, setRepLoading] = useState(false);

  // Watch form values for conditional logic
  const panelDataWatch = watch();

  const loadExistingReps = useCallback(async () => {
    try {
      const response = await panelAPI.getPanelReps(createdPanelId);
      if (response.success && response.reps) {
        // Transform panelMembers to rep format
        const transformedReps = response.reps.map((panelMember) => ({
          userId: panelMember.userId,
          name: panelMember.user?.name || "Unknown",
          email: panelMember.user?.email || "",
          repRole: panelMember.repRole,
          addedAt: panelMember.createdAt || new Date().toISOString(),
        }));
        setReps(transformedReps);
      }
    } catch (error) {
      console.error("Error loading existing reps:", error);
    }
  }, [createdPanelId]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const response = await cadreAPI.getAvailableCadreMembers();
        console.log("response", response);
        if (!isMounted) return;
        const members = response?.members || [];
        // Normalize into the shape used locally
        const normalized = members.map((m) => ({
          id: m.id,
          name: m.name,
          email: m.email,
          expertise: m.cadreProfile?.expertise || [],
          yearsExperience: m.cadreProfile?.yearsExperience || 0,
          region: m.cadreProfile?.region || "",
          availability: m.cadreProfile?.availability || "AVAILABLE",
          rating: Number(m.cadreProfile?.rating || 0),
          panelsCompleted: m.cadreProfile?.panelsCompleted || 0,
        }));
        setAvailableCadre(normalized);
        setFilteredCadre(normalized);
      } catch (e) {
        console.error("Failed to load available cadre members:", e);
        setAvailableCadre([]);
        setFilteredCadre([]);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  // Load existing reps when panel is created
  useEffect(() => {
    if (createdPanelId) {
      loadExistingReps();
    }
  }, [createdPanelId, loadExistingReps]);

  const toggleSection = (sectionName) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }));
  };

  const handleExpertiseFilter = (expertise) => {
    setFilterCriteria((prev) => ({
      ...prev,
      expertise: prev.expertise.includes(expertise)
        ? prev.expertise.filter((e) => e !== expertise)
        : [...prev.expertise, expertise],
    }));
  };

  const applyFilters = () => {
    let filtered = [...availableCadre];

    // Filter by expertise
    if (filterCriteria.expertise.length > 0) {
      filtered = filtered.filter((cadre) =>
        cadre.expertise.some((exp) => filterCriteria.expertise.includes(exp))
      );
    }

    // Filter by years of experience
    if (filterCriteria.yearsExperience) {
      const minYears = parseInt(filterCriteria.yearsExperience);
      filtered = filtered.filter((cadre) => cadre.yearsExperience >= minYears);
    }

    // Filter by region
    if (filterCriteria.region) {
      filtered = filtered.filter(
        (cadre) => cadre.region === filterCriteria.region
      );
    }

    setFilteredCadre(filtered);
  };

  const handleCadreSelection = (cadreId) => {
    setSelectedCadre((prev) =>
      prev.includes(cadreId)
        ? prev.filter((id) => id !== cadreId)
        : [...prev, cadreId]
    );
  };

  const selectAllFiltered = () => {
    const allFilteredIds = filteredCadre.map((c) => c.id);
    setSelectedCadre(allFilteredIds);
  };

  const clearSelection = () => {
    setSelectedCadre([]);
  };

  const saveAsDraft = (data) => {
    const draftPanel = {
      ...data,
      panelStatus: "DRAFT",
      id: Date.now(),
    };

    // Save to localStorage
    const existingPanels = JSON.parse(
      localStorage.getItem("draftPanels") || "[]"
    );
    existingPanels.push(draftPanel);
    localStorage.setItem("draftPanels", JSON.stringify(existingPanels));

    toast.success("Panel saved as draft successfully!");
  };

  const onSubmitCreatePanel = async (data) => {
    try {
      const panelPayload = {
        ...data,
        panelStatus: data.panelStatus || "CREATED",
      };

      // Create panel via API
      const response = await panelAPI.createPanel(panelPayload);

      if (response.success) {
        setCreatedPanelId(response.panel.id);
        toast.success("Panel created successfully!");

        // Navigate to next step or panel details
        if (currentStep < 3) {
          setCurrentStep(2);
        } else {
          navigate(`/panel-details/${response.panel.id}`);
        }
      } else {
        throw new Error(response.message || "Failed to create panel");
      }
    } catch (error) {
      console.error("Error creating panel:", error);

      let errorMessage = "Unknown error";

      if (error.response && error.response.data) {
        errorMessage =
          error.response.data.message ||
          error.response.data.error ||
          JSON.stringify(error.response.data);
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(` ${errorMessage}`);
    }
  };

  const submitShortlist = async () => {
    try {
      setShortlistLoading(true);

      if (!createdPanelId) {
        toast.warning("Please create the panel first");
        return;
      }

      // Assign selected members to the panel via API
      const assignmentPromises = selectedCadre.map((memberId) => {
        const member = availableCadre.find((c) => c.id === memberId);
        if (member) {
          return panelAPI
            .assignMember(createdPanelId, memberId, "MEMBER")
            .catch((error) => {
              console.error(`Failed to assign member ${member.name}:`, error);
              return null;
            });
        }
        return null;
      });

      await Promise.all(assignmentPromises);

      // Send panel assignment notifications to selected cadre members
      const notificationPromises = selectedCadre.map((memberId) => {
        const member = availableCadre.find((c) => c.id === memberId);
        if (member) {
          return notificationService
            .sendPanelAssignmentEmail(member, panelDataWatch)
            .catch((error) => {
              console.error(
                `Failed to send notification to ${member.name}:`,
                error
              );
              return null;
            });
        }
        return null;
      });

      await Promise.all(notificationPromises);

      // Update panel status to indicate shortlist submitted
      await panelAPI.updatePanelStatus(createdPanelId, "Active");

      toast.success(
        "Cadre members assigned successfully! Proceed to add representatives."
      );
      setCurrentStep(3);
    } catch (error) {
      console.error("Error submitting shortlist:", error);
      toast.error(
        "Failed to submit shortlist: " + (error.message || "Unknown error")
      );
    } finally {
      setShortlistLoading(false);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  // Step 4: Add Reps handlers
  const handleAddRep = async (repData) => {
    try {
      setRepLoading(true);
      if (!createdPanelId) {
        toast.error("Please create the panel first");
        return;
      }

      const response = await panelAPI.createAndAddRep(createdPanelId, repData);
      if (response.success && response.panelMember) {
        // Transform panelMember to rep format for consistency
        const rep = {
          userId: response.panelMember.userId,
          name: response.panelMember.user?.name || "Unknown",
          email: response.panelMember.user?.email || "",
          repRole: response.panelMember.repRole || "",
          addedAt: response.panelMember.createdAt || new Date().toISOString(),
        };
        setReps([...reps, rep]);
        toast.success("Rep added successfully!");
        setShowAddRepModal(false);
      }
    } catch (error) {
      console.error("Error adding rep:", error);
      toast.error(error.response?.data?.message || "Failed to add rep");
    } finally {
      setRepLoading(false);
    }
  };

  const handleInviteRep = async (inviteData) => {
    if (!createdPanelId) {
      toast.error("Please create the panel first");
      throw new Error("Panel not created");
    }

    try {
      setRepLoading(true);

      const response = await panelAPI.addRepToPanel(
        createdPanelId,
        parseInt(inviteData.userId),
        inviteData.repRole
      );

      if (response.success && response.panelMember) {
        // Transform panelMember to rep format for consistency
        const rep = {
          userId: response.panelMember.userId,
          name: response.panelMember.user?.name || "Unknown",
          email: response.panelMember.user?.email || "",
          repRole: response.panelMember.repRole,
          addedAt: response.panelMember.createdAt || new Date().toISOString(),
        };
        setReps((prevReps) => [...prevReps, rep]);
        return rep;
      } else {
        throw new Error(response.message || "Failed to invite rep");
      }
    } catch (error) {
      console.error("Error inviting rep:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to invite rep";
      // Only show error if it's not a duplicate error (silently skip duplicates)
      if (!errorMessage.includes("already")) {
        toast.error(errorMessage);
      }
      throw error;
    } finally {
      setRepLoading(false);
    }
  };

  const handleRemoveRep = async (userId) => {
    try {
      if (!createdPanelId) {
        toast.error("Panel not found");
        return;
      }

      const response = await panelAPI.removeRepFromPanel(
        createdPanelId,
        userId
      );
      if (response.success) {
        setReps(reps.filter((rep) => rep && rep.userId !== userId));
        toast.success("Rep removed successfully!");
      }
    } catch (error) {
      console.error("Error removing rep:", error);
      toast.error("Failed to remove rep");
    }
  };

  // Get current rep counts
  const getCurrentRepCounts = () => {
    const communityReps = reps.filter(
      (rep) => rep && rep.repRole === "COMMUNITY_MEMBER"
    );
    const clientReps = reps.filter(
      (rep) => rep && rep.repRole === "CLIENT_MEMBER"
    );
    return {
      community: communityReps.length,
      client: clientReps.length,
    };
  };

  // Get rep requirements for current panel type
  const getRepRequirements = () => {
    const panelType = panelDataWatch.panelType;
    if (!panelType) return null;
    return PROJECT_TYPE_REP_REQUIREMENTS[panelType];
  };

  // Check if rep requirements are met
  const areRepRequirementsMet = () => {
    const requirements = getRepRequirements();
    if (!requirements) return false;

    const current = getCurrentRepCounts();
    return (
      current.community === requirements.communityReps &&
      current.client === requirements.clientReps
    );
  };

  // Check if can add more reps of specific type
  const canAddRep = (repRole) => {
    const requirements = getRepRequirements();
    if (!requirements) return false;

    const current = getCurrentRepCounts();

    if (repRole === "COMMUNITY_MEMBER") {
      return current.community < requirements.communityReps;
    } else if (repRole === "CLIENT_MEMBER") {
      return current.client < requirements.clientReps;
    }

    return false;
  };

  // Validate reps based on panel type (blocking validation)
  const validateReps = () => {
    const panelType = panelDataWatch.panelType;
    if (!panelType) {
      toast.error("Panel type must be selected before adding representatives.");
      return false;
    }

    const requirements = getRepRequirements();
    if (!requirements) {
      toast.error("Invalid panel type selected.");
      return false;
    }

    const current = getCurrentRepCounts();

    if (!areRepRequirementsMet()) {
      toast.error(
        `Rep requirements not met. Required: ${requirements.communityReps} Community + ${requirements.clientReps} Client. Current: ${current.community} Community + ${current.client} Client.`
      );
      return false;
    }

    return true;
  };

  const proceedToReview = () => {
    // Move from Add Reps (step 3) to Review & Submit (step 4)
    setCurrentStep(4);
  };

  const finishPanel = () => {
    // Validate reps before finishing
    if (!validateReps()) {
      return;
    }

    toast.success("Panel creation completed successfully!");
    navigate(`/panel-details/${createdPanelId}`);
  };

  return (
    <div>
      <div className="bg-gray-50 min-h-[calc(100vh-200px)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              Panel Creation & Selection
            </h1>
            <p className="text-gray-600 mt-2">
              Create a new panel and select cadre members
            </p>
          </div>

          {/* Progress Steps */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center justify-between">
              {[
                { num: 1, label: "Panel Details" },
                { num: 2, label: "Select Cadre" },
                { num: 3, label: "Add Reps" },
                { num: 4, label: "Review & Submit" },
              ].map((step, index) => (
                <div key={step.num} className="flex items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                      currentStep >= step.num
                        ? "bg-custom-blue text-white"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {currentStep > step.num ? "✓" : step.num}
                  </div>
                  <div className="ml-3">
                    <p
                      className={`text-sm font-medium ${
                        currentStep >= step.num
                          ? "text-gray-900"
                          : "text-gray-500"
                      }`}
                    >
                      {step.label}
                    </p>
                  </div>
                  {index < 3 && (
                    <div
                      className={`ml-4 w-20 h-0.5 ${
                        currentStep > step.num
                          ? "bg-custom-blue"
                          : "bg-gray-300"
                      }`}
                    ></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step 1: Panel Creation Form */}
          {currentStep === 1 && (
            <div className="bg-white rounded-lg shadow p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                Step 1: Panel Details
              </h2>

              <div className="space-y-6">
                {/* Panel Info Section */}
                <div className="border border-gray-200 rounded-lg">
                  <button
                    type="button"
                    onClick={() => toggleSection("panelInfo")}
                    className="w-full px-6 py-4 flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors rounded-t-lg"
                  >
                    <h3 className="text-lg font-semibold text-gray-900">
                      Panel Information
                    </h3>
                    <svg
                      className={`w-5 h-5 text-gray-600 transform transition-transform ${
                        collapsedSections.panelInfo ? "" : "rotate-180"
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {!collapsedSections.panelInfo && (
                    <div className="p-6 space-y-6">
                      <Controller
                        name="panelName"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="Panel Name"
                            field={field}
                            name="panelName"
                            type="text"
                            placeholder="Enter panel name"
                            required
                            error={errors.panelName}
                          />
                        )}
                      />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Controller
                          name="panelStatus"
                          control={control}
                          render={({ field }) => (
                            <Dropdown
                              headLabel="Status"
                              label="Select status"
                              options={PANEL_STATUS_OPTIONS}
                              value={field.value}
                              onSelect={field.onChange}
                              error={errors.panelStatus}
                            />
                          )}
                        />

                        <Controller
                          name="panelId"
                          control={control}
                          render={({ field }) => (
                            <Input
                              label="Panel ID"
                              field={field}
                              name="panelId"
                              type="text"
                              placeholder="Enter panel ID"
                              required
                              error={errors.panelId}
                            />
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Controller
                          name="panelType"
                          control={control}
                          render={({ field }) => (
                            <Dropdown
                              headLabel="Panel Type"
                              label="Select panel type"
                              options={PANEL_TYPE_OPTIONS}
                              value={field.value}
                              onSelect={field.onChange}
                              error={errors.panelType}
                            />
                          )}
                        />

                        <Controller
                          name="femaRegion"
                          control={control}
                          render={({ field }) => (
                            <Dropdown
                              headLabel="FEMA Region"
                              label="Select FEMA region"
                              options={FEMA_REGION_OPTIONS}
                              value={field.value}
                              onSelect={field.onChange}
                              error={errors.femaRegion}
                            />
                          )}
                        />
                      </div>

                      <Controller
                        name="panelRequestDate"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="Panel Request Date"
                            field={field}
                            name="panelRequestDate"
                            type="date"
                            error={errors.panelRequestDate}
                          />
                        )}
                      />
                    </div>
                  )}
                </div>

                {/* Summary Section */}
                <div className="border border-gray-200 rounded-lg">
                  <button
                    type="button"
                    onClick={() => toggleSection("summary")}
                    className="w-full px-6 py-4 flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors rounded-t-lg"
                  >
                    <h3 className="text-lg font-semibold text-gray-900">
                      Summary
                    </h3>
                    <svg
                      className={`w-5 h-5 text-gray-600 transform transition-transform ${
                        collapsedSections.summary ? "" : "rotate-180"
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {!collapsedSections.summary && (
                    <div className="p-6 space-y-6">
                      <Controller
                        name="panelConvened"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="Panel Convened"
                            field={field}
                            name="panelConvened"
                            type="date"
                            error={errors.panelConvened}
                          />
                        )}
                      />

                      <Controller
                        name="requestSummary"
                        control={control}
                        render={({ field }) => (
                          <Textarea
                            {...field}
                            label="Request Summary"
                            placeholder="Enter detailed request summary..."
                            rows={6}
                            error={errors.requestSummary?.message}
                          />
                        )}
                      />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Controller
                          name="packageLinkUrl"
                          control={control}
                          render={({ field }) => (
                            <Input
                              label="Package Link URL"
                              field={field}
                              name="packageLinkUrl"
                              type="url"
                              placeholder="https://example.com/package"
                              error={errors.packageLinkUrl}
                            />
                          )}
                        />

                        <Controller
                          name="packageLinkText"
                          control={control}
                          render={({ field }) => (
                            <Input
                              label="Package Link Text"
                              field={field}
                              name="packageLinkText"
                              type="text"
                              placeholder="e.g., View Package Details"
                              error={errors.packageLinkText}
                            />
                          )}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Decision Section */}
                <div className="border border-gray-200 rounded-lg">
                  <button
                    type="button"
                    onClick={() => toggleSection("decision")}
                    className="w-full px-6 py-4 flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors rounded-t-lg"
                  >
                    <h3 className="text-lg font-semibold text-gray-900">
                      Decision
                    </h3>
                    <svg
                      className={`w-5 h-5 text-gray-600 transform transition-transform ${
                        collapsedSections.decision ? "" : "rotate-180"
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {!collapsedSections.decision && (
                    <div className="p-6 space-y-6">
                      <Controller
                        name="panelDecisionDate"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="Panel Decision Date"
                            field={field}
                            name="panelDecisionDate"
                            type="date"
                            error={errors.panelDecisionDate}
                          />
                        )}
                      />

                      <Controller
                        name="decisionSummary"
                        control={control}
                        render={({ field }) => (
                          <Textarea
                            {...field}
                            label="Decision Summary"
                            placeholder="Enter decision summary..."
                            rows={6}
                            error={errors.decisionSummary?.message}
                          />
                        )}
                      />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Controller
                          name="reportLinkUrl"
                          control={control}
                          render={({ field }) => (
                            <Input
                              label="Report Link URL"
                              field={field}
                              name="reportLinkUrl"
                              type="url"
                              placeholder="https://example.com/report"
                              error={errors.reportLinkUrl}
                            />
                          )}
                        />

                        <Controller
                          name="reportLinkText"
                          control={control}
                          render={({ field }) => (
                            <Input
                              label="Report Link Text"
                              field={field}
                              name="reportLinkText"
                              type="text"
                              placeholder="e.g., View Report"
                              error={errors.reportLinkText}
                            />
                          )}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Final Determination Section */}
                <div className="border border-gray-200 rounded-lg">
                  <button
                    type="button"
                    onClick={() => toggleSection("finalDetermination")}
                    className="w-full px-6 py-4 flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors rounded-t-lg"
                  >
                    <h3 className="text-lg font-semibold text-gray-900">
                      Final Determination
                    </h3>
                    <svg
                      className={`w-5 h-5 text-gray-600 transform transition-transform ${
                        collapsedSections.finalDetermination ? "" : "rotate-180"
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {!collapsedSections.finalDetermination && (
                    <div className="p-6 space-y-6">
                      <Controller
                        name="finalDeterminationDate"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="Final Determination Date"
                            field={field}
                            name="finalDeterminationDate"
                            type="date"
                            error={errors.finalDeterminationDate}
                          />
                        )}
                      />

                      <Controller
                        name="femaFinalDeterminationSummary"
                        control={control}
                        render={({ field }) => (
                          <Textarea
                            {...field}
                            label="FEMA Final Determination Summary"
                            placeholder="Enter FEMA final determination summary..."
                            rows={6}
                            error={
                              errors.femaFinalDeterminationSummary?.message
                            }
                          />
                        )}
                      />
                    </div>
                  )}
                </div>

                {/* Shortlist Filters Section */}
                <div className="border border-gray-200 rounded-lg">
                  <button
                    type="button"
                    onClick={() => toggleSection("shortlistFilters")}
                    className="w-full px-6 py-4 flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors rounded-t-lg"
                  >
                    <h3 className="text-lg font-semibold text-gray-900">
                      Shortlist Filters
                    </h3>
                    <svg
                      className={`w-5 h-5 text-gray-600 transform transition-transform ${
                        collapsedSections.shortlistFilters ? "" : "rotate-180"
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {!collapsedSections.shortlistFilters && (
                    <div className="p-6 space-y-6">
                      {/* State to Exclude */}
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="text-md font-semibold text-gray-900 mb-4">
                          State to Exclude
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <Controller
                            name="excludeCountry"
                            control={control}
                            render={({ field }) => (
                              <Input
                                label="Country"
                                field={field}
                                name="excludeCountry"
                                type="text"
                                required
                                classes="bg-gray-50"
                                disabled
                              />
                            )}
                          />

                          <Controller
                            name="excludeState"
                            control={control}
                            render={({ field }) => (
                              <Dropdown
                                headLabel="State"
                                label="Select state to exclude"
                                options={US_STATES}
                                value={field.value}
                                onSelect={field.onChange}
                                required
                                error={errors.excludeState}
                              />
                            )}
                          />
                        </div>
                      </div>

                      {/* Shortlist Expertise */}
                      <Controller
                        name="shortlistExpertise"
                        control={control}
                        render={({ field }) => (
                          <div>
                            <p className="block text-sm font-medium text-gray-700 mb-3">
                              Shortlist Expertise
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {EXPERTISE_OPTIONS.map((expertise) => (
                                <label
                                  key={expertise}
                                  className="flex items-center px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg hover:bg-gray-100 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={
                                      field.value?.includes(expertise) || false
                                    }
                                    onChange={(e) => {
                                      const newValue = e.target.checked
                                        ? [...(field.value || []), expertise]
                                        : (field.value || []).filter(
                                            (exp) => exp !== expertise
                                          );
                                      field.onChange(newValue);
                                    }}
                                    className="mr-2"
                                  />
                                  <span className="text-sm text-gray-700">
                                    {expertise}
                                  </span>
                                </label>
                              ))}
                            </div>
                            {errors.shortlistExpertise && (
                              <p className="text-sm text-red-500 mt-1">
                                {errors.shortlistExpertise.message}
                              </p>
                            )}
                          </div>
                        )}
                      />

                      <Controller
                        name="shortlistApproved"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="Shortlist Approved (Date & Time)"
                            field={field}
                            name="shortlistApproved"
                            type="datetime-local"
                            error={errors.shortlistApproved}
                          />
                        )}
                      />

                      <Controller
                        name="shortlistEmailNotified"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="Shortlist Email Notified"
                            field={field}
                            name="shortlistEmailNotified"
                            type="text"
                            placeholder="Enter email notification details"
                            error={errors.shortlistEmailNotified}
                          />
                        )}
                      />
                    </div>
                  )}
                </div>

                {/* Review Dates Section */}
                <div className="border border-gray-200 rounded-lg">
                  <button
                    type="button"
                    onClick={() => toggleSection("reviewDates")}
                    className="w-full px-6 py-4 flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors rounded-t-lg"
                  >
                    <h3 className="text-lg font-semibold text-gray-900">
                      Review Dates
                    </h3>
                    <svg
                      className={`w-5 h-5 text-gray-600 transform transition-transform ${
                        collapsedSections.reviewDates ? "" : "rotate-180"
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {!collapsedSections.reviewDates && (
                    <div className="p-6 space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Controller
                          name="cadreMemberResponseDate"
                          control={control}
                          render={({ field }) => (
                            <Input
                              label="Cadre Member Response Date"
                              field={field}
                              name="cadreMemberResponseDate"
                              type="date"
                              error={errors.cadreMemberResponseDate}
                            />
                          )}
                        />

                        <Controller
                          name="repSelectionDate"
                          control={control}
                          render={({ field }) => (
                            <Input
                              label="Rep Selection Date"
                              field={field}
                              name="repSelectionDate"
                              type="date"
                              error={errors.repSelectionDate}
                            />
                          )}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Published Checkbox */}
                <Controller
                  name="isPublished"
                  control={control}
                  render={({ field }) => (
                    <div className="border border-gray-200 rounded-lg p-6">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={field.value || false}
                          onChange={(e) => field.onChange(e.target.checked)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-700">
                          Published
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 mt-2 ml-7">
                        Check this box to make the panel visible to all users
                      </p>
                    </div>
                  )}
                />
              </div>

              <div className="flex justify-between mt-8 pt-6 border-t">
                <Button
                  type="button"
                  onClick={() => saveAsDraft(panelDataWatch)}
                  variant="outline"
                  size="md"
                >
                  Save as Draft
                </Button>

                <div className="flex space-x-3">
                  <Button
                    type="button"
                    onClick={() => navigate("/dashboard")}
                    variant="outline"
                    size="md"
                  >
                    Cancel
                  </Button>

                  <Button
                    type="button"
                    onClick={handleSubmit(onSubmitCreatePanel)}
                    disabled={
                      isSubmitting ||
                      !panelDataWatch.panelName ||
                      !panelDataWatch.panelId
                    }
                    variant="primary"
                    size="md"
                  >
                    {isSubmitting ? "Creating..." : "Create Panel & Continue"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Cadre Selection */}
          {currentStep === 2 && (
            <div className="bg-white rounded-lg shadow p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                Step 2: Select Cadre Members
              </h2>

              {/* Filter Criteria */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="font-semibold text-gray-900 mb-4">
                  Filter Criteria
                </h3>

                <div className="space-y-4">
                  <div>
                    <p className="block text-sm font-medium text-gray-700 mb-2">
                      Expertise Areas
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {EXPERTISE_OPTIONS.map((expertise) => (
                        <button
                          key={expertise}
                          type="button"
                          onClick={() => handleExpertiseFilter(expertise)}
                          className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                            filterCriteria.expertise.includes(expertise)
                              ? "bg-custom-blue text-white"
                              : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {expertise}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="filter-years-experience"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Minimum Years of Experience
                      </label>
                      <input
                        id="filter-years-experience"
                        type="number"
                        value={filterCriteria.yearsExperience}
                        onChange={(e) =>
                          setFilterCriteria((prev) => ({
                            ...prev,
                            yearsExperience: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-light-blue focus:border-transparent"
                        placeholder="e.g., 10"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="filter-region"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Region
                      </label>
                      <select
                        id="filter-region"
                        value={filterCriteria.region}
                        onChange={(e) =>
                          setFilterCriteria((prev) => ({
                            ...prev,
                            region: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-light-blue focus:border-transparent"
                      >
                        <option value="">All Regions</option>
                        <option value="Northeast">Northeast</option>
                        <option value="Southeast">Southeast</option>
                        <option value="Midwest">Midwest</option>
                        <option value="Southwest">Southwest</option>
                        <option value="West Coast">West Coast</option>
                        <option value="Gulf Coast">Gulf Coast</option>
                      </select>
                    </div>
                  </div>

                  <Button onClick={applyFilters} variant="primary" size="md">
                    Apply Filters
                  </Button>
                </div>
              </div>

              {/* Cadre List */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-gray-900">
                    Available Cadre Members ({filteredCadre.length})
                  </h3>
                  <div className="flex space-x-2">
                    <Button
                      onClick={selectAllFiltered}
                      variant="outline"
                      size="sm"
                    >
                      Select All
                    </Button>
                    <Button
                      onClick={clearSelection}
                      variant="outline"
                      size="sm"
                    >
                      Clear Selection
                    </Button>
                  </div>
                </div>

                <div className="space-y-3 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4">
                  {filteredCadre.map((cadre) => (
                    <div
                      key={cadre.id}
                      className={`p-4 rounded-lg border transition-colors ${
                        selectedCadre.includes(cadre.id)
                          ? "border-custom-blue bg-blue-50"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          checked={selectedCadre.includes(cadre.id)}
                          onChange={() => handleCadreSelection(cadre.id)}
                          className="mt-1 mr-3"
                        />
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-semibold text-gray-900">
                                {cadre.name}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {cadre.email}
                              </p>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {cadre.expertise.map((exp) => (
                                  <span
                                    key={exp}
                                    className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                                  >
                                    {exp}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="flex items-center text-sm text-gray-600">
                                <svg
                                  className="w-4 h-4 text-yellow-400 mr-1"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                                {cadre.rating}
                              </div>
                              <p className="text-sm text-gray-600">
                                {cadre.yearsExperience} years exp.
                              </p>
                              <p className="text-sm text-gray-600">
                                {cadre.region}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {cadre.panelsCompleted} panels
                              </p>
                              <span
                                className={`inline-block mt-1 text-xs px-2 py-1 rounded ${
                                  cadre.availability === "Available"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-yellow-100 text-yellow-800"
                                }`}
                              >
                                {cadre.availability}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-900">
                  <strong>Selected:</strong> {selectedCadre.length} cadre
                  members
                </p>
              </div>

              <div className="flex justify-between pt-6 border-t">
                <Button onClick={prevStep} variant="outline" size="md">
                  Previous
                </Button>

                <div className="flex space-x-3">
                  <Button
                    onClick={() => navigate("/dashboard")}
                    variant="outline"
                    size="md"
                  >
                    Cancel
                  </Button>

                  <Button
                    onClick={submitShortlist}
                    disabled={selectedCadre.length === 0 || shortlistLoading}
                    variant="primary"
                    size="md"
                    className="bg-emerald-800 hover:bg-emerald-900"
                  >
                    {shortlistLoading ? "Submitting..." : "Submit Shortlist"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Add Reps */}
          {currentStep === 3 && (
            <div className="bg-white rounded-lg shadow p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                Step 3: Add Representatives
              </h2>

              {/* Panel Type Requirements Info */}
              {panelDataWatch.panelType && getRepRequirements() && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h3 className="font-semibold text-blue-900">
                    {panelDataWatch.panelType} Panel
                  </h3>
                  <p className="text-sm text-blue-800">
                    Community: {getCurrentRepCounts().community}/
                    {getRepRequirements().communityReps} | FEMA:{" "}
                    {getCurrentRepCounts().client}/
                    {getRepRequirements().clientReps === 999
                      ? "∞"
                      : getRepRequirements().clientReps}
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    {getRepRequirements().description}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3 mb-6">
                <Button
                  variant="primary"
                  onClick={() => setShowInviteRepModal(true)}
                  disabled={
                    !createdPanelId ||
                    !panelDataWatch.panelType ||
                    areRepRequirementsMet()
                  }
                  title={
                    !createdPanelId
                      ? "Please create the panel first"
                      : !panelDataWatch.panelType
                      ? "Please select a panel type first"
                      : areRepRequirementsMet()
                      ? "All required representatives have been added"
                      : ""
                  }
                >
                  <svg
                    className="w-4 h-4 mr-2 inline"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                    />
                  </svg>
                  Invite Existing Rep
                </Button>

                <Button
                  variant="secondary"
                  onClick={() => setShowAddRepModal(true)}
                  disabled={
                    !createdPanelId ||
                    !panelDataWatch.panelType ||
                    areRepRequirementsMet()
                  }
                  title={
                    !createdPanelId
                      ? "Please create the panel first"
                      : !panelDataWatch.panelType
                      ? "Please select a panel type first"
                      : areRepRequirementsMet()
                      ? "All required representatives have been added"
                      : ""
                  }
                >
                  <svg
                    className="w-4 h-4 mr-2 inline"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add New Rep
                </Button>
              </div>

              {!createdPanelId && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 text-yellow-600 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> Please complete Step 1 (Panel
                      Details) and create the panel before adding
                      representatives.
                    </p>
                  </div>
                </div>
              )}

              {/* Reps Table */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-4">
                  Added Representatives ({reps.length})
                </h3>
                <ReusableTable
                  columns={[
                    {
                      key: "name",
                      label: "Name",
                      render: (rep) => (
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {rep.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {rep.email}
                          </div>
                        </div>
                      ),
                    },
                    {
                      key: "repRole",
                      label: "Role",
                      render: (rep) => {
                        const roleColors = {
                          COMMUNITY_MEMBER: "bg-green-100 text-green-800",
                          CLIENT_MEMBER: "bg-purple-100 text-purple-800",
                        };
                        const roleLabels = {
                          COMMUNITY_MEMBER: "Community Member",
                          CLIENT_MEMBER: "FEMA Member",
                        };
                        return (
                          <span
                            className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                              roleColors[rep.repRole] ||
                              "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {roleLabels[rep.repRole] || rep.repRole}
                          </span>
                        );
                      },
                    },
                    {
                      key: "addedAt",
                      label: "Added",
                      render: (rep) =>
                        new Date(rep.addedAt).toLocaleDateString(),
                      cellClassName: "text-gray-500",
                    },
                    {
                      key: "actions",
                      label: "Actions",
                      render: (rep) => (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRemoveRep(rep.userId)}
                        >
                          Remove
                        </Button>
                      ),
                    },
                  ]}
                  data={reps.filter((rep) => rep)}
                  emptyStateMessage="No representatives added yet. Click 'Invite' or 'Add' to begin."
                />
              </div>

              <div className="flex justify-between pt-6 border-t">
                <Button onClick={prevStep} variant="outline" size="md">
                  Previous
                </Button>

                <div className="flex space-x-3">
                  <Button
                    onClick={() => navigate("/dashboard")}
                    variant="outline"
                    size="md"
                  >
                    Cancel
                  </Button>

                  <Button
                    onClick={proceedToReview}
                    variant="primary"
                    size="md"
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={!areRepRequirementsMet()}
                    title={
                      !areRepRequirementsMet()
                        ? "Please add all required representatives before proceeding"
                        : ""
                    }
                  >
                    Proceed to Review
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review & Submit */}
          {currentStep === 4 && (
            <div className="bg-white rounded-lg shadow p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                Step 4: Review & Submit
              </h2>

              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                <div className="flex items-start">
                  <svg
                    className="w-6 h-6 text-green-600 mr-3 flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-green-900">
                      Panel Setup Complete!
                    </h3>
                    <p className="text-green-800 mt-1">
                      All cadre members and representatives have been assigned.
                      Review the details below and submit the panel for
                      activation.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                {/* Panel Information */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">
                    Panel Information
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Panel Name:</span>
                      <span className="font-medium">
                        {panelDataWatch.panelName}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Panel ID:</span>
                      <span className="font-medium">
                        {panelDataWatch.panelId}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Panel Type:</span>
                      <span className="font-medium">
                        {panelDataWatch.panelType || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Selected Cadre:</span>
                      <span className="font-medium">
                        {selectedCadre.length} members
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        Added Representatives:
                      </span>
                      <span className="font-medium">
                        {reps.length} representatives
                      </span>
                    </div>
                  </div>
                </div>

                {/* Selected Cadre Members */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">
                    Selected Cadre Members ({selectedCadre.length})
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    {selectedCadre.length > 0 ? (
                      <div className="space-y-2">
                        {selectedCadre.map((cadreId) => {
                          const cadre = availableCadre.find(
                            (c) => c.id === cadreId
                          );
                          return cadre ? (
                            <div
                              key={cadreId}
                              className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0"
                            >
                              <div>
                                <span className="font-medium text-gray-900">
                                  {cadre.name}
                                </span>
                                <span className="text-sm text-gray-500 ml-2">
                                  ({cadre.email})
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {cadre.expertise.slice(0, 2).map((exp) => (
                                  <span
                                    key={exp}
                                    className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded"
                                  >
                                    {exp}
                                  </span>
                                ))}
                                {cadre.expertise.length > 2 && (
                                  <span className="text-xs text-gray-500">
                                    +{cadre.expertise.length - 2} more
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : null;
                        })}
                      </div>
                    ) : (
                      <p className="text-gray-500">No cadre members selected</p>
                    )}
                  </div>
                </div>

                {/* Added Representatives */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">
                    Added Representatives ({reps.length})
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    {reps.length > 0 ? (
                      <div className="space-y-2">
                        {reps
                          .filter((rep) => rep)
                          .map((rep) => {
                            const roleColors = {
                              COMMUNITY_MEMBER: "bg-green-100 text-green-800",
                              CLIENT_MEMBER: "bg-purple-100 text-purple-800",
                            };
                            const roleLabels = {
                              COMMUNITY_MEMBER: "Community Member",
                              CLIENT_MEMBER: "FEMA Member",
                            };
                            return (
                              <div
                                key={rep.userId}
                                className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0"
                              >
                                <div>
                                  <span className="font-medium text-gray-900">
                                    {rep.name}
                                  </span>
                                  <span className="text-sm text-gray-500 ml-2">
                                    ({rep.email})
                                  </span>
                                </div>
                                <span
                                  className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                                    roleColors[rep.repRole] ||
                                    "bg-gray-100 text-gray-800"
                                  }`}
                                >
                                  {roleLabels[rep.repRole] || rep.repRole}
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <p className="text-gray-500">No representatives added</p>
                    )}
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">
                    Final Summary
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-blue-800">
                        Total Panel Members:
                      </span>
                      <span className="ml-2 font-semibold text-blue-900">
                        {selectedCadre.length + reps.length}
                      </span>
                    </div>
                    <div>
                      <span className="text-blue-800">Panel Status:</span>
                      <span className="ml-2 font-semibold text-blue-900">
                        Ready for Activation
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-between mt-8 pt-6 border-t">
                <Button onClick={prevStep} variant="outline" size="md">
                  Previous
                </Button>

                <div className="flex space-x-3">
                  <Button
                    onClick={() => navigate("/dashboard")}
                    variant="outline"
                    size="md"
                  >
                    Cancel
                  </Button>

                  <Button
                    onClick={finishPanel}
                    variant="primary"
                    size="md"
                    className="bg-emerald-800 hover:bg-emerald-900"
                  >
                    Finish & View Panel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Modals */}
          <AddRepModal
            isOpen={showAddRepModal}
            onClose={() => setShowAddRepModal(false)}
            onSubmit={handleAddRep}
            loading={repLoading}
            canAddRep={canAddRep}
            requirements={getRepRequirements()}
          />

          <InviteRepModal
            isOpen={showInviteRepModal}
            onClose={() => setShowInviteRepModal(false)}
            onSubmit={handleInviteRep}
            loading={repLoading}
            canAddRep={canAddRep}
            requirements={getRepRequirements()}
          />
        </div>
      </div>
    </div>
  );
};

export default PanelCreationEnhanced;
